<template>
	<div class="container w-100" data-cy="veracross-component">
		<v-card class="mb-4">
			<v-card-title>Veracross Configuration</v-card-title>
			<v-card-text>
				<v-form>
					<v-row>
						<v-col cols="12" md="6">
							<v-text-field
								:model-value="config.school_route"
								label="School route"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-school-route"
								@update:model-value="update({ school_route: String($event ?? '') })"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								:model-value="config.client_id"
								label="Client ID"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-client-id"
								@update:model-value="update({ client_id: String($event ?? '') })"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								:model-value="config.client_secret"
								label="Client secret"
								type="password"
								autocomplete="new-password"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-client-secret"
								@update:model-value="update({ client_secret: String($event ?? '') })"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6">
							<v-select
								:model-value="config.school_id"
								:items="schoolOptions"
								item-title="school_name"
								item-value="school_id"
								label="School"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-school"
								:menu-props="{ ['data-cy' as string]: 'veracross-school-menu' }"
								@update:model-value="update({ school_id: $event ?? null })"
							></v-select>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								:model-value="config.school_year"
								label="School year (optional)"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-school-year"
								@update:model-value="update({ school_year: String($event ?? '') })"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6">
							<v-select
								:model-value="config.parent_filter"
								:items="veracrossParentFilters"
								item-title="title"
								item-value="value"
								label="Parent filter"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-parent-filter"
								:menu-props="{
									['data-cy' as string]: 'veracross-parent-filter-menu',
								}"
								@update:model-value="setParentFilter"
							></v-select>
						</v-col>
					</v-row>
				</v-form>
			</v-card-text>
		</v-card>
		<div class="mb-4" data-cy="veracross-connection-test">
			<v-btn
				type="button"
				color="primary"
				variant="outlined"
				:loading="testing"
				:disabled="testing"
				data-cy="test-veracross-connection-btn"
				@click="testConnection"
			>
				Test connection
			</v-btn>
			<p
				v-if="connectionError"
				class="text-error mt-3 mb-0"
				role="alert"
				data-cy="veracross-connection-error"
			>
				{{ connectionError }}
			</p>
			<ul
				v-if="scopeRows.length"
				class="veracross-scope-results mt-3"
				data-cy="veracross-connection-scopes"
			>
				<li
					v-for="row in scopeRows"
					:key="row.scope"
					data-cy="veracross-scope-result"
					:data-scope="row.scope"
					:data-granted="row.granted ? 'true' : 'false'"
				>
					<v-icon
						:color="row.granted ? 'success' : 'error'"
						size="small"
						aria-hidden="true"
					>
						{{ row.granted ? "mdi-check" : "mdi-close" }}
					</v-icon>
					<span>{{ row.granted ? "Granted" : "Missing" }}</span>
					<span>{{ row.scope }}</span>
				</li>
			</ul>
			<p
				v-if="optionalWarning"
				class="mt-3 mb-0"
				role="status"
				data-cy="veracross-connection-warning"
			>
				{{ optionalWarning }}
			</p>
		</div>
		<ecsTaskSync integration_type="veracross" :has-valid-config="syncAllowed" />
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import EcsTaskSync from "@/components/school/integration/ecsTaskSync.vue";
import {
	veracrossParentFilters,
	type VeracrossConfig,
	type VeracrossParentFilter,
} from "@/components/school/integration/utils";
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";
import { useDistrictStore } from "@/stores/district";
import { useSchoolStore } from "@/stores/school";

/** Nine scopes that must be granted before Sync. Optional scopes come from the API. */
const REQUIRED_SCOPES = [
	"students:list",
	"staff_faculty:list",
	"parents:list",
	"contact_info:list",
	"relationships:list",
	"academics.classes:list",
	"academics.courses:list",
	"academics.enrollments:list",
	"academics.config.grading_periods:list",
] as const;

const props = defineProps<{
	config: VeracrossConfig;
	hasValidConfig: boolean;
	loading: boolean;
}>();

const emit = defineEmits<{
	(e: "update:config", value: VeracrossConfig): void;
}>();

const districtStore = useDistrictStore();
const schoolStore = useSchoolStore();
const alertsStore = useAlertsStore();

const testing = ref(false);
const connectionOk = ref(false);
const connectionError = ref("");
const scopeRows = ref<{ scope: string; granted: boolean }[]>([]);
const missingOptional = ref<string[]>([]);

const syncAllowed = computed(() => props.hasValidConfig && connectionOk.value);

const optionalWarning = computed(() =>
	missingOptional.value.length
		? `Co-teachers won't sync. Missing: ${missingOptional.value.join(", ")}.`
		: ""
);

const schoolOptions = computed(() => {
	const options: { school_id: number; school_name: string }[] = [];
	const add = (id?: number | null, name?: string | null) => {
		if (!id || options.some((option) => option.school_id === id)) return;
		options.push({
			school_id: id,
			school_name: name?.trim() ? name : `School ${id}`,
		});
	};

	add(schoolStore.school?.school_id, schoolStore.school?.school_name);
	for (const school of districtStore.schools || []) {
		add(school.school_id, school.school_name);
	}
	add(props.config.school_id);
	return options;
});

const update = (patch: Partial<VeracrossConfig>) => {
	emit("update:config", { ...props.config, ...patch });
};

const setParentFilter = (value: VeracrossParentFilter) => {
	update({ parent_filter: value });
};

const clearConnectionResult = () => {
	connectionOk.value = false;
	connectionError.value = "";
	scopeRows.value = [];
	missingOptional.value = [];
};

const stringList = (value: unknown): string[] =>
	Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

const applyTestResponse = (res: unknown) => {
	clearConnectionResult();
	if (!res || typeof res !== "object") {
		connectionError.value = "Could not test the Veracross connection.";
		alertsStore.addAlert("error", connectionError.value);
		return;
	}

	const body = res as Record<string, unknown>;
	const granted = new Set(stringList(body.granted_scopes));
	const missing = new Set(stringList(body.missing_scopes));
	const hasScopeLists =
		Array.isArray(body.granted_scopes) || Array.isArray(body.missing_scopes);

	if (hasScopeLists) {
		scopeRows.value = REQUIRED_SCOPES.map((scope) => ({
			scope,
			granted: granted.has(scope) && !missing.has(scope),
		}));
	}
	missingOptional.value = stringList(body.missing_optional_scopes);

	if (body.ok === true) {
		connectionOk.value = true;
		if (missingOptional.value.length) {
			alertsStore.addAlert("info", optionalWarning.value);
		}
		return;
	}

	const error = typeof body.error === "string" ? body.error.trim() : "";
	if (error) {
		connectionError.value = error;
		alertsStore.addAlert("error", error);
		return;
	}
	if (!hasScopeLists) {
		connectionError.value = "Could not test the Veracross connection.";
		alertsStore.addAlert("error", connectionError.value);
	}
};

const testConnection = async () => {
	testing.value = true;
	clearConnectionResult();
	try {
		const body: {
			school_route?: string;
			client_id?: string;
			client_secret?: string;
		} = {};
		const schoolRoute = props.config.school_route.trim();
		const clientId = props.config.client_id.trim();
		if (schoolRoute) body.school_route = schoolRoute;
		if (clientId) body.client_id = clientId;
		if (props.config.client_secret) body.client_secret = props.config.client_secret;

		const res = await FetchService.fetchData("/integration/veracross/test", "post", body);
		applyTestResponse(res);
	} catch {
		connectionError.value = "Could not test the Veracross connection.";
		alertsStore.addAlert("error", connectionError.value);
	} finally {
		testing.value = false;
	}
};

watch(schoolOptions, (options) => {
	if (!props.config.school_id && options.length === 1) {
		update({ school_id: options[0].school_id });
	}
});

watch(
	() => [props.config.school_route, props.config.client_id, props.config.client_secret],
	(next, prev) => {
		if (next[0] === prev?.[0] && next[1] === prev?.[1] && next[2] === prev?.[2]) return;
		clearConnectionResult();
	}
);

onMounted(async () => {
	await districtStore.fetchSchools();
	if (!props.config.school_id && schoolOptions.value.length === 1) {
		update({ school_id: schoolOptions.value[0].school_id });
	}
});
</script>

<style scoped lang="scss">
.veracross-scope-results {
	list-style: none;
	padding-left: 0;
	margin-bottom: 0;

	li {
		display: flex;
		align-items: center;
		gap: 8px;
	}
}
</style>
